export const PROJECT_NAME_INFO = `
  The **project name** serves as a unique identifier for your Deployment as a Service project. 
  It is used in various contexts such as naming the ArgoCD project, Kubernetes resources, and more. 
  Choosing a clear and descriptive name can help with organization and management of your projects.

  ℹ️ **Note**: If you are creating a **DaaS Project** for **KaaP**, we recommend using the same name as the **KaaP Tenant** to maintain consistency across systems.

  **Naming Rules:**
  - Must be unique across all projects
  - Can only contain lowercase letters, numbers, and hyphens
  - Must start with a letter
  - Must end with a letter or number
  - Maximum length of 46 characters

`;

export const PROJECT_DESCRIPTION_INFO = `
  A brief description of the project. This is an optional field that can be used to provide additional context about the project, its purpose, or any other relevant information.

`;

export const OWNER_INFO = `
  The **owner** field specifies the team Supervisory Organization Group responsible for the project. 
  This information is important for accountability, communication, and access control purposes.
`;

export const SECURITY_GROUPS_INFO = `
  **Security groups** define the access permissions for the project. By selecting appropriate security groups, you can control who has access to view, edit, or delete the project for both **Frontstage** and **ArgoCD**. 
  It is important to choose security groups that align with your organization's access control policies to ensure that only authorized personnel can manage the project.
`;

export const CICD_OIDC_SUBJECTS_INFO = `
  **CI/CD OIDC subject claims** allow GitHub Actions workflows and Azure DevOps pipelines to read and synchronise this project's applications in ArgoCD, without a long-lived token.

  Each entry is the \`sub\` claim of the OIDC token that the pipeline presents. Listing a subject grants it Application get and sync permissions.

  **GitHub Actions**

  Please refer to the official [GitHub OIDC subject claims](https://docs.github.com/en/actions/reference/security/oidc) documentation. Both legacy and immutable subject claim formats are supported.

  - Only the \`tomtom-internal\` (org ID: 70519145) GitHub organisation is accepted.

  \`\`\`
  repo:tomtom-internal/my-repo:ref:refs/heads/main
  repo:tomtom-internal@70519145/my-repo@456789:ref:refs/heads/main
  \`\`\`

  **Azure DevOps**

  The subject comes from a [workload identity federation](https://learn.microsoft.com/en-us/azure/devops/pipelines/release/configure-workload-identity) service connection. Everything except the trailing service connection ID is fixed, and wildcards are not accepted.

  \`\`\`
  /eid1/c/pub/t/JoBPN1R7Okq4fTKPom7BDQ/a/rISbSSETf0KqFyZ8ppdXmA/sc/6bd365a9-7e90-4df8-8786-c2f3fef91acf/SERVICE_CONNECTION_ID
  \`\`\`

  To read the value from a pipeline, request a token for the service connection and decode its \`sub\`:

  \`\`\`
  curl -sS -X POST -H "Authorization: Bearer $SYSTEM_ACCESSTOKEN" -H 'Content-Type: application/json' -d '{}' "$SYSTEM_OIDCREQUESTURI?api-version=7.1&serviceConnectionId=<SERVICE_CONNECTION_ID>"
  \`\`\`

  **Rules**
  - Each subject must be unique within this project.
  - Maximum 255 characters per subject.
`;

export const NOTIFICATIONS_INFO = `
  **Notifications** allows to configure Slack notifications, Grafana annotations (works with [Grafana Cloud](https://grafana.tomtomgroup.com/)) and GitHub commit statuses for receiving updates about changes in application state.
  - **Slack notifications**: You can specify a Slack channel to receive notifications about project's applications and application sets state changes. This helps keep your team informed about the status of the project without needing to constantly check the dashboard.
  - **Grafana annotations**: By enabling Grafana annotations, you can have important events and state changes automatically annotated in your Grafana dashboards, providing better visibility into the project's lifecycle and aiding in troubleshooting and monitoring efforts.
  - **GitHub commit statuses**: When an application finishes syncing successfully and becomes healthy, ArgoCD posts a commit status named \`daas-argocd/<project>/<application>\` against the deployed commit. A GitHub Actions workflow can wait on that status, or react to the \`status\` webhook event, to run only once a deployment has actually landed. For applications with multiple sources the status is posted to the second source, which by DaaS convention is the values repository.
`;

export const SLACK_CHANNEL_INFO = `
  The **Slack channel** field is used to specify the name of the Slack channel where notifications about project's applications and application sets state changes will be sent. 
  This allows your team to stay informed about important updates and events related to the project without needing to constantly check the dashboard.
`;

export const KAAP_CLUSTER_INFO = `
  Select a KaaP (Kubernetes as a Platform) tenant to use as a deployment target for this ArgoCD project.

  **What is KaaP?**
  KaaP is a managed Kubernetes platform service that provides fully managed Kubernetes clusters 
  without requiring teams to handle cluster operations, maintenance, or upgrades. It's designed 
  to let teams focus on deploying applications rather than managing infrastructure.

  **What is a Tenant?**
  A tenant is your team's logical workspace within the KaaP ecosystem. When you create a tenant, 
  KaaP automatically assigns it to an underlying Kubernetes cluster - but you don't need to worry 
  about which specific cluster. KaaP handles all the cluster management behind the scenes.

  **How it works:**
  - You select a tenant from the dropdown
  - The tenant is automatically bound to a KaaP-managed cluster
  - Namespace pattern is automatically configured as \`kaap-<tenant-name>-*\`
  - All cluster connection details and configurations are handled by KaaP
  - Your applications deploy to namespaces matching your tenant's pattern

  **Access Control:**

  **You can only see tenants you own** - The dropdown retrieves tenants from the KaaP API, 
  which returns only tenants where you (or your team) are listed as owners. This ensures proper 
  access control and prevents unauthorized use of other teams' resources.

  **Ownership Model:**
  - Tenant ownership is managed within KaaP
  - KaaP API enforces access control and returns only your authorized tenants
  - If you don't see a tenant, you need to be granted ownership through KaaP

  **What Happens After Selection:**
  - ArgoCD project is configured to deploy to your tenant's cluster
  - Namespace restrictions are automatically set to \`kaap-<tenant-name>-*\`
  - You cannot customize namespace patterns (enforced by KaaP for security)
  - Applications in this project can deploy to any namespace matching the tenant pattern

  **Common Use Cases:**
  - **Application teams**: Focus on application deployment, not infrastructure
  - **Rapid development**: Quick setup without cluster provisioning
  - **Standardized environments**: Consistent configuration across teams
  - **Reduced operational burden**: Let platform team handle cluster operations

  **PIM Access:**
  If the selected tenant has PIM (Privileged Identity Management) enabled, the DaaS project will
  automatically be configured with PIM access. Users will have read-only access to the ArgoCD UI by
  default, and must activate PIM to make changes such as syncing applications.

  **Troubleshooting:**
  - **Don't see your tenant?** Verify you have ownership in KaaP or contact the tenant owner
  - **Need a new tenant?** Create new tenant through KaaP provisioning process

  ℹ️ **Note**: Namespace restrictions are automatically set and cannot be customized for KaaP
  clusters. This is by design to maintain security and consistency across the platform.
`;

export const SELF_MANAGED_CLUSTER_INFO = `
  Select an AKS (Azure Kubernetes Service) cluster from TomTom organization to assign to this ArgoCD project.

  **What are Self-Managed Clusters?**
  Self-managed clusters are AKS clusters from TomTom Azure tenant that have been 
  ingested into the Frontstage catalog as \`kubernetes-cluster\` entities. These clusters are 
  managed outside of KaaP and can be added to ArgoCD projects for application deployment.

  **How it works:**
  - Clusters are automatically discovered and ingested from your Azure tenant
  - Each cluster appears as a Frontstage catalog entity with complete metadata
  - When you select a cluster, all necessary connection details are automatically configured
  - The service principal \`daas-argo\` must have \`Azure Kubernetes Service RBAC Cluster Admin\` role on the cluster
  - The cluster becomes a valid deployment destination for applications in this ArgoCD project

  **Access Control:**

  **You can only see clusters you own** - The dropdown displays only clusters where you (or one 
  of your assigned groups) are listed as the owner in the Frontstage catalog. This ensures users 
  can only assign clusters they have permission to manage.

  **Ownership Model:**
  - Cluster ownership is determined by the \`spec.owner\` field in the catalog entity
  - If you belong to a group that owns the cluster, you can select it
  - This prevents unauthorized access to clusters in other teams or environments

  **What Happens After Selection:**
  - ArgoCD project is configured with cluster connection details
  - Applications in the project can deploy to this cluster
  - Namespace restrictions you define will apply to this cluster
  - Users must also be granted access to the ArgoCD project to deploy

  **Common Use Cases:**
  - **Team-specific clusters**: Each team manages their own AKS clusters
  - **Environment separation**: Different clusters for dev, staging, production
  - **Regional deployment**: Select clusters in specific Azure regions
  - **Compliance requirements**: Use clusters with specific security configurations

  **Troubleshooting:**
  - **Don't see your cluster?** Check if you or your groups are listed as owners in the catalog
  - **Need access?** Contact the cluster owner to be added to the ownership group
  - **Cluster not listed at all?** Verify it exists in Azure and is properly ingested

  ℹ️ **Note**: This is different from KaaP clusters, which are managed centrally and have 
  predefined configurations. Self-managed clusters give you full control but require proper 
  ownership and permissions.
`;

export const NAMESPACE_INFO = `
  Define namespace access patterns for this cluster in your ArgoCD project configuration.

  **What are Namespace Restrictions?**
  Namespace restrictions control which Kubernetes namespaces applications in this ArgoCD project 
  can deploy to on the selected cluster. This is a critical security feature that prevents 
  unauthorized deployments and enforces organizational boundaries.

  **IMPORTANT:** Customizing namespace restrictions not available for KaaP clusters.
  It will be prefilled with the namespace pattern based on the Tenant Name, e.g. \`kaap-<tenant-name>-*\`.

  **How it works:**
  - Each entry defines an allowed namespace or namespace pattern
  - Applications can only deploy to namespaces matching these patterns
  - Multiple patterns can be specified for flexibility
  - Wildcards (\`*\`) enable pattern-based matching

  **Pattern Types:**

  **Exact Match** - Allow specific namespace only
  \`\`\`
  production
  my-app-prod
  \`\`\`

  **Prefix Pattern** - Allow all namespaces starting with a prefix
  \`\`\`
  dev-*        → matches: dev-app1, dev-app2, dev-frontend
  team-data-*  → matches: team-data-api, team-data-etl
  \`\`\`

  **Suffix Pattern** - Allow all namespaces ending with a suffix
  \`\`\`
  *-staging    → matches: app-staging, frontend-staging
  *-test       → matches: api-test, worker-test
  \`\`\`

  **Complex Patterns** - Combine wildcards for flexibility
  \`\`\`
  *-prod-*     → matches: app-prod-eu, service-prod-us
  \`\`\`

  **Common Patterns:**

  **Environment-based:**
  - \`dev-*\` - All development namespaces
  - \`staging-*\` - All staging namespaces
  - \`prod-*\` or \`*-prod\` - All production namespaces

  **Team-based:**
  - \`team-platform-*\` - Platform team namespaces
  - \`team-data-*\` - Data team namespaces
  - \`*-shared\` - Shared resources across teams

  **Application-based:**
  - \`myapp-*\` - All namespaces for specific application
  - \`*-backend\`, \`*-frontend\` - Service-type based

  **Best Practices:**
  - Use wildcards to allow flexibility while maintaining control
  - Be as specific as possible to minimize security risks
  - Separate environments with distinct patterns (e.g., \`dev-*\` vs \`prod-*\`)
  - Document your namespace naming conventions

  For more details, refer to the [ArgoCD Project Specification](https://argo-cd.readthedocs.io/en/stable/operator-manual/project-specification/).
`;
